import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getPostBySlug } from './posts';

// Code block component with syntax highlighting styling
const CodeBlock = ({ code, language }) => (
  <div className="my-6 rounded-lg overflow-hidden bg-ink text-cream-100 shadow-lg">
    {language && (
      <div className="px-4 py-2 bg-ink-light/20 text-xs uppercase tracking-widest text-cream-200 border-b border-ink-light/20">
        {language}
      </div>
    )}
    <pre className="p-4 overflow-x-auto text-sm leading-relaxed">
      <code className="font-mono">{code}</code>
    </pre>
  </div>
);

// Simple markdown-like renderer for basic formatting
const renderContent = (content) => {
  const elements = [];
  const lines = content.split('\n');
  
  let currentParagraph = [];
  let inList = false;
  let listItems = [];
  let inCodeBlock = false;
  let codeBlockContent = [];
  let codeBlockLanguage = '';

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const text = currentParagraph.join(' ').trim();
      if (text) {
        elements.push(
          <p key={elements.length} className="mb-6">
            {renderInlineFormatting(text)}
          </p>
        );
      }
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={elements.length} className="list-disc pl-6 mb-6 space-y-2">
          {listItems.map((item, i) => (
            <li key={i}>{renderInlineFormatting(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };

  const flushCodeBlock = () => {
    if (codeBlockContent.length > 0) {
      elements.push(
        <CodeBlock 
          key={elements.length} 
          code={codeBlockContent.join('\n')} 
          language={codeBlockLanguage}
        />
      );
      codeBlockContent = [];
      codeBlockLanguage = '';
      inCodeBlock = false;
    }
  };

  const renderInlineFormatting = (text) => {
    // Handle bold, italic, links, and inline code
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      // Check for inline code `code`
      const codeMatch = remaining.match(/`([^`]+)`/);
      // Check for links [text](url)
      const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
      // Check for bold **text**
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
      // Check for italic *text* (single asterisk)
      const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);

      const matches = [
        codeMatch && { type: 'code', match: codeMatch, index: codeMatch.index },
        linkMatch && { type: 'link', match: linkMatch, index: linkMatch.index },
        boldMatch && { type: 'bold', match: boldMatch, index: boldMatch.index },
        italicMatch && { type: 'italic', match: italicMatch, index: italicMatch.index },
      ].filter(Boolean).sort((a, b) => a.index - b.index);

      if (matches.length === 0) {
        parts.push(remaining);
        break;
      }

      const first = matches[0];
      if (first.index > 0) {
        parts.push(remaining.slice(0, first.index));
      }

      if (first.type === 'code') {
        parts.push(
          <code key={key++} className="bg-cream-200 text-ink px-1.5 py-0.5 rounded font-mono text-sm">
            {first.match[1]}
          </code>
        );
      } else if (first.type === 'link') {
        parts.push(
          <a
            key={key++}
            href={first.match[2]}
            className="editorial-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {first.match[1]}
          </a>
        );
      } else if (first.type === 'bold') {
        parts.push(<strong key={key++} className="font-medium text-ink">{first.match[1]}</strong>);
      } else if (first.type === 'italic') {
        parts.push(<em key={key++}>{first.match[1]}</em>);
      }

      remaining = remaining.slice(first.index + first.match[0].length);
    }

    return parts;
  };

  lines.forEach((line) => {
    // Handle code block start/end
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        flushCodeBlock();
      } else {
        flushList();
        flushParagraph();
        inCodeBlock = true;
        codeBlockLanguage = line.trim().slice(3).trim();
      }
      return;
    }

    // Inside code block
    if (inCodeBlock) {
      codeBlockContent.push(line);
      return;
    }

    const trimmed = line.trim();

    // Handle headers
    if (trimmed.startsWith('### ')) {
      flushList();
      flushParagraph();
      elements.push(
        <h3 key={elements.length} className="font-serif text-xl md:text-2xl text-ink mt-10 mb-4">
          {trimmed.slice(4)}
        </h3>
      );
      return;
    }

    if (trimmed.startsWith('## ')) {
      flushList();
      flushParagraph();
      elements.push(
        <h2 key={elements.length} className="font-serif text-2xl md:text-3xl text-ink mt-12 mb-6">
          {trimmed.slice(3)}
        </h2>
      );
      return;
    }

    // Handle list items
    if (trimmed.startsWith('- ')) {
      flushParagraph();
      inList = true;
      listItems.push(trimmed.slice(2));
      return;
    }

    // Empty line
    if (trimmed === '') {
      if (inList) {
        flushList();
      }
      flushParagraph();
      return;
    }

    // Regular text
    if (inList) {
      flushList();
    }
    currentParagraph.push(trimmed);
  });

  flushList();
  flushParagraph();
  flushCodeBlock();

  return elements;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-cream-100 relative">
      {/* Grain texture overlay */}
      <div className="grain-overlay" aria-hidden="true"></div>

      {/* Header */}
      <header className="pt-16 md:pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="animate-on-load animate-fade-in-up">
            <Link
              to="/blog"
              className="font-body text-ink-muted hover:text-ink text-sm uppercase tracking-widest mb-6 inline-block transition-colors"
            >
              ← All Posts
            </Link>
            <time className="block font-body text-ink-muted text-sm uppercase tracking-widest mb-4">
              {formattedDate}
            </time>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink tracking-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-6 md:px-12 pb-24">
        <div className="max-w-3xl mx-auto">
          <div className="editorial-divider mb-12"></div>
          
          <article className="font-body text-ink-light leading-relaxed text-lg animate-on-load animate-fade-in-up animate-delay-200">
            {renderContent(post.content)}
          </article>

          <div className="editorial-divider my-12"></div>
          
          <div className="text-center">
            <Link
              to="/blog"
              className="inline-block font-body text-accent hover:text-accent-dark transition-colors"
            >
              ← Back to all posts
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-cream-300 bg-cream-200/50">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
          <p className="font-body text-center text-ink-muted text-sm tracking-wide">
            © {new Date().getFullYear()} Teddy Marchildon
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
